import { Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useDropzone } from 'react-dropzone'
import { useFormContext } from 'react-hook-form'
import { batch, useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** CONSTANT */
import { imgs } from 'constants/images'
import { colors } from 'constants/theme'

/** COMPONENT */
import InputTextField from 'component/Input/InputTextField'

/** HOOKS */
import { CardReaderPayload, dataUrlToFile, useCardReader } from 'features/counter/register/hooks/useCardReader'
import { resolveThaiAddress } from 'features/counter/register/hooks/resolveThaiAddress'

/** SLICE */
import { setPatient } from 'app/slice/register/register.slice'

export interface FormRetrieveProp {
  disabled?: boolean
  error?: any
  resetErrorMessage?: () => void
}

const CardProfile = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  width: 123,
  height: 123,
  flexDirection: 'column',
  alignItems: 'center',
  outline: 'none',
  cursor: 'default',
  border: `5px solid ${colors.black12}`,
  borderRadius: '50%',
  [theme.breakpoints.down('sm')]: {
    width: 107,
    height: 107
  },
  img: {
    width: '100%',
    height: '100%'
  }
}))

export default function FormRetrieve(props: FormRetrieveProp) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { register, setValue } = useFormContext()

  const [inputs, setInputs]: any = useState([])

  /** PROFILE */
  const [imgData, setImgData]: any = useState()
  const [fileProfile, setFileProfile] = useState<any>(null)

  /** CARD READER STATUS */
  const [readerError, setReaderError] = useState<string>('')
  const lastAppliedSigRef = useRef<string>('')
  const lastAddressSigRef = useRef<string>('')

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: ['.jpeg', '.jpg', '.png', '.webp'],
    multiple: false,
    onDrop: (acceptedFiles) => {
      uploadImage(acceptedFiles)
      setImgData(URL.createObjectURL(acceptedFiles[0]))
    }
  })

  const handleChange = (event: any) => {
    const name = event.target.name
    const value = event.target.value
    setInputs((values: any) => ({ ...values, [name]: value }))
  }

  const uploadImage = (files: any) => {
    if (files[0]) {
      const file = files[0]
      const fileName = file.name.split('.')
      const detailImage = {
        fileId: null,
        base64: '',
        fileExtension: fileName[fileName.length - 1],
        fileSize: file.size.toString()
      }
      const reader: any = new FileReader()
      reader.addEventListener('load', () => {
        /** reader.result is base64 */
        const base64 = reader.result.split(',')
        setImgData(reader.result)
        detailImage.base64 = base64.length > 0 ? base64[base64.length - 1] : reader.result
      })
      setFileProfile(file)
      reader.readAsDataURL(file)
    }
  }

  useEffect(() => {
    window.addEventListener('paste', (e: any) => {
      if (e.clipboardData.files.length) {
        if (e.clipboardData.files && e.clipboardData.files[0]) {
          uploadImage(e.clipboardData.files)
          setValue('retrieve.profile', e.clipboardData.files)
        }
      }
    })
  }, [])

  const applyCardData = useCallback((payload: CardReaderPayload) => {
    // Agents may rebroadcast the same payload — apply only when it actually changes.
    let sig = ''
    try { sig = JSON.stringify(payload) } catch { sig = '' }
    if (sig && sig === lastAppliedSigRef.current) return
    lastAppliedSigRef.current = sig

    setReaderError((prev) => (prev ? '' : prev))

    const TEXT_KEYS: (keyof CardReaderPayload)[] = [
      'prefixId', 'firstname', 'lastname', 'firstnameEn', 'lastnameEn',
      'idCard', 'gender', 'dateOfBirth', 'address', 'soi', 'road',
      'province', 'district', 'subDistrict', 'signature',
    ]

    const nextInputs: Record<string, any> = {}
    TEXT_KEYS.forEach((key) => {
      const value = payload[key]
      if (value === undefined) return
      const safe = value ?? ''
      setValue(`retrieve.${key}`, safe)
      nextInputs[key] = safe
    })
    if (Object.keys(nextInputs).length) {
      setInputs((prev: any) => ({ ...prev, ...nextInputs }))
    }

    if (payload.profile) {
      const profile = payload.profile
      if (typeof profile === 'string' && profile.startsWith('data:')) {
        const ext = /data:image\/(\w+)/i.exec(profile)?.[1]?.toLowerCase() || 'jpg'
        const file = dataUrlToFile(profile, `id-card-profile.${ext}`)
        if (file) {
          setImgData(profile)
          setFileProfile(file)
        }
      } else if (profile) {
        setImgData(profile as any)
      }
    }

    // Resolve province/district/subDistrict text → IDs and dispatch directly to
    // the patient slice. SelectAddress's text-based resolution can miss when
    // the master list and card text don't share an exact substring (e.g.
    // master "กรุงเทพ" vs card "กรุงเทพมหานคร"); doing the lookup here with a
    // bidirectional fuzzy match makes the address dropdowns populate reliably
    // and pre-loads the district/subDistrict API results before submit.
    const province = payload.province ?? ''
    const district = payload.district ?? ''
    const subDistrict = payload.subDistrict ?? ''
    const addressSig = `${province}|${district}|${subDistrict}`
    if (addressSig.replace(/\|/g, '') && addressSig !== lastAddressSigRef.current) {
      lastAddressSigRef.current = addressSig
      resolveThaiAddress(province, district, subDistrict)
        .then((resolved) => {
          // Batch all four ID dispatches into one render so SelectAddress's
          // [patientId, provinceId] effect sees districtId/subDistrictId/postcode
          // populated when it fires — otherwise the effect runs on the
          // provinceId change alone and zeroes the dependent fields.
          batch(() => {
            if (resolved.provinceId) {
              dispatch(setPatient({ key: 'provinceId', value: resolved.provinceId }))
            }
            if (resolved.districtId) {
              dispatch(setPatient({ key: 'districtId', value: resolved.districtId }))
            }
            if (resolved.subDistrictId) {
              dispatch(setPatient({ key: 'subDistrictId', value: resolved.subDistrictId }))
            }
            if (resolved.postcode) {
              dispatch(setPatient({ key: 'postcode', value: resolved.postcode }))
            }
          })
        })
        .catch(() => { /* fall back to SelectAddress text resolution */ })
    }
  }, [setValue, dispatch])

  const handleReaderError = useCallback((err: { code?: string; message?: string }) => {
    setReaderError(err?.message || err?.code || '')
  }, [])

  const { status: readerStatus } = useCardReader({
    enabled: true,
    onCardData: applyCardData,
    onError: handleReaderError,
  })

  const readerStatusText =
    readerError
      ? readerError
      : readerStatus === 'connecting'
        ? t('REGISTER.MODAL.CARD_READER.CONNECTING')
        : readerStatus === 'connected'
          ? t('REGISTER.MODAL.CARD_READER.CONNECTED')
          : readerStatus === 'error'
            ? t('REGISTER.MODAL.CARD_READER.ERROR')
            : ''
  const readerStatusColor =
    readerError || readerStatus === 'error'
      ? colors.statusInactive
      : readerStatus === 'connected'
        ? colors.themeMainColor
        : colors.textLightGray

  return (
    <Modal.Body className={'show-grid py-2 px-0'}>
      {readerStatusText && (
        <div style={{ fontSize: 14, color: readerStatusColor, marginBottom: 8, paddingLeft: 4 }}>
          {readerStatusText}
        </div>
      )}
      <Grid container>
        <input type="hidden" {...register('retrieve')} value={inputs} />
        <input type="hidden" {...register('retrieve.profile', { value: [fileProfile] })} />
        <Grid item md={'auto'} className={'m-auto'}>
          <CardProfile {...getRootProps({ isDragActive, isDragAccept, isDragReject })} className={'card-profile'}>
            <input {...getInputProps()} disabled={true} />
            {!imgData ? (
              <div className={'personal-figure position-relative'}>
                <img src={imgs.defaultAvatar} className={'personal-avatar'} alt="avatar" />
              </div>
            ) : (
              <img src={imgData} alt="imageData" className={'personal-avatar'} />
            )}
          </CardProfile>
        </Grid>
      </Grid>

      <Grid container className={'justify-content-start d-flex align-content-center text-left mt-2'} spacing={2}>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.prefixId', { onChange: handleChange })} value={inputs.prefixId} label={t('REGISTER.FORM.PREFIXES')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.firstname', { onChange: handleChange })} hookValue={true} value={inputs.firstname} label={t('REGISTER.FORM.FIRST_NAME_TH')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.lastname', { onChange: handleChange })} value={inputs.lastname} label={t('REGISTER.FORM.LAST_NAME_TH')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.firstnameEn', { onChange: handleChange })} hookValue={true} value={inputs.firstnameEn} label={t('REGISTER.FORM.FIRST_NAME')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.lastnameEn', { onChange: handleChange })} value={inputs.lastnameEn} label={t('REGISTER.FORM.LAST_NAME')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.idCard', { onChange: handleChange })} value={inputs.idCard} label={t('REGISTER.FORM.ID_CARD')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.gender', { onChange: handleChange })} value={inputs.gender} label={t('REGISTER.FORM.GENDER')} size="small" />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField type="text" onchange={handleChange} register={register('retrieve.dateOfBirth', { onChange: handleChange })} value={inputs.dateOfBirth} label={t('REGISTER.FORM.BIRTHDAY')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.address', { onChange: handleChange })} value={inputs.address} label={t('REGISTER.FORM.ADDRESS')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.soi', { onChange: handleChange })} value={inputs.soi} label={t('REGISTER.FORM.ALLEY')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.road', { onChange: handleChange })} value={inputs.road} label={t('REGISTER.FORM.ROAD')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.province', { onChange: handleChange })} value={inputs.province} label={t('REGISTER.FORM.PROVINCE')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField onchange={handleChange} register={register('retrieve.district', { onChange: handleChange })} value={inputs.district} label={t('REGISTER.FORM.DISTRICT')} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} className={'w-100'}>
          <InputTextField key="treatmentCourseTypeNameEn" onchange={handleChange} register={register('retrieve.subDistrict', { onChange: handleChange })} value={inputs.subDistrict} label={t('REGISTER.FORM.SUB_DISTRICT')} />
          <div className={'d-none'}>
            <input type="file" id="document_attachment_doc" />
          </div>
        </Grid>
      </Grid>
    </Modal.Body>
  )
}
